/**
 * Community home — the entry page for the community surface.
 *
 * Shows the seeded categories with their descriptions and link to each
 * category page. This PR ships the minimal home; subsequent UI PRs will
 * add recent-activity widgets, the directory carousel, and unread
 * notification context.
 *
 * Mirrors MyJournalPage's LoadState data-loading pattern.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import { fetchCategories, type CategoryRead } from "@/lib/community-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; categories: CategoryRead[] }
  | { kind: "not_member" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface CommunityHomePageProps {
  onUnauthenticated?: (returnTo: string) => void;
  fetchFn?: typeof fetchCategories;
}

export function CommunityHomePage({
  onUnauthenticated = redirectToLogin,
  fetchFn = fetchCategories,
}: CommunityHomePageProps = {}): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const categories = await fetchFn();
        if (cancelled) return;
        setState({ kind: "ready", categories });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/community");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : "Could not load the community categories.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, onUnauthenticated]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-4xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading the community…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-3xl font-bold text-foreground">
          The CaiT Community
        </h1>
        <p className="mt-4 text-muted-foreground">
          The community is open to active learners and certified
          practitioners. Join the cohort or earn the credential to
          participate.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/course"
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Continue the course
          </Link>
          <a
            href="https://clinicianassist.ai/cait"
            className="inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Join the cohort
          </a>
        </div>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <header>
        <h1 className="text-3xl font-bold text-foreground">
          The CaiT Community
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A learning-support space for active learners and certified
          practitioners. Ask questions, swap notes, find peers.
        </p>
      </header>

      <ul className="mt-10 space-y-4">
        {state.categories.map((cat) => (
          <li key={cat.id}>
            <Link
              to={`/community/c/${cat.slug}`}
              className="block rounded-lg border border-border bg-white p-6 hover:border-primary hover:shadow-sm transition-all"
            >
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="text-lg font-semibold text-foreground">
                  {cat.title}
                </h2>
                <div className="flex gap-2">
                  {cat.is_announcement && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                      Announcements
                    </span>
                  )}
                  {cat.requires_certification && (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                      Certified posting
                    </span>
                  )}
                </div>
              </div>
              {cat.description && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {cat.description}
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
